import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import * as R from 'ramda';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import AccordionWrapper from '../AccordionWrapper/AccordionWrapper';
import useFetchPatientProfileNotesQuery from 'App/hooks/useFetchPatientProfileNotesQuery';
import { stripAllHtml } from 'App/utils';
import truncateText from 'App/utils/truncateText';

// Custom header component for the accordion
const ImportantNotesHeader = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #666;
    margin-bottom: 10px;

    .caret-icon {
        margin-right: 8px;
        transition: transform 0.2s ease;
        font-size: 12px;

        &.expanded {
            transform: rotate(90deg);
        }
    }

    span {
        margin: 0;
        user-select: none;
    }

    &:hover {
        opacity: 0.8;
    }
`;

// Custom header component for the accordion
const CustomHeaderContent = ({ onClick, title, isVisible, setIsVisible, className }) => (
    <ImportantNotesHeader
        className={className}
        onClick={() => {
            setIsVisible(!isVisible);
            onClick?.(isVisible);
        }}
    >
        <i className={`fa fa-caret-right caret-icon ${isVisible ? 'expanded' : ''}`} />
        <span>{title}</span>
    </ImportantNotesHeader>
);

CustomHeaderContent.propTypes = {
    onClick: PropTypes.func,
    title: PropTypes.string.isRequired,
    isVisible: PropTypes.bool.isRequired,
    setIsVisible: PropTypes.func.isRequired,
    className: PropTypes.string,
};

CustomHeaderContent.defaultProps = {
    onClick: null,
    className: '',
};

const NotesList = styled.ul`
    margin: 0;
    padding-left: 20px;

    li {
        margin-bottom: 8px;
        line-height: 1.4;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const TooltipPopover = styled(Popover)`
    background-color: white;
    border: 1px solid #ccc;
    max-width: 300px;

    .popover-body {
        padding: 8px 12px;
        font-size: 14px;
        line-height: 1.4;
    }
`;

const TruncatedNoteText = styled.span`
    cursor: ${(props) => (props.isTruncated ? 'help' : 'default')};
`;

const propTypes = {
    patientId: PropTypes.string.isRequired,
};

const defaultProps = {};

const PatientImportantNotesAccordion = ({ patientId }) => {
    const { data } = useFetchPatientProfileNotesQuery(patientId);

    const importantNotes = useMemo(() => {
        if (!data) return [];

        return R.pipe(
            R.filter((note) => !!note.important),
            R.sortBy(R.prop('updatedOn')),
            R.reverse(),
            R.take(5)
        )(data);
    }, [data]);

    // Don't render when no important notes exist.
    if (!importantNotes?.length) {
        return null;
    }

    const notesContent = (
        <NotesList>
            {importantNotes.map((note) => {
                const cleanNote = stripAllHtml(note.note);
                const truncatedNote = truncateText(cleanNote, 100);
                const isTruncated = cleanNote.length > 100;

                if (isTruncated) {
                    const tooltipPopover = (
                        <TooltipPopover id={`note-tooltip-${note.uuid}`}>
                            <Popover.Content>{cleanNote}</Popover.Content>
                        </TooltipPopover>
                    );

                    return (
                        <li key={note.uuid}>
                            <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={tooltipPopover}>
                                <TruncatedNoteText isTruncated>{truncatedNote}</TruncatedNoteText>
                            </OverlayTrigger>
                        </li>
                    );
                }

                return (
                    <li key={note.uuid}>
                        <TruncatedNoteText isTruncated={false}>{truncatedNote}</TruncatedNoteText>
                    </li>
                );
            })}
        </NotesList>
    );

    return (
        <AccordionWrapper
            title="Important Notes"
            stateKey="sidebarImportantNotes-toggle"
            HeaderContent={CustomHeaderContent}
        >
            {notesContent}
        </AccordionWrapper>
    );
};

PatientImportantNotesAccordion.propTypes = propTypes;
PatientImportantNotesAccordion.defaultProps = defaultProps;

export default PatientImportantNotesAccordion;
